import { CalculationType, IBayesianNetwork } from '../bayesian-networks-interfaces';
import {
    addInUseRule, areNodesIndependent,
    findMarginComplementRules,
    getRulesForMarginalExpansion,
    handeInfiniteLoop,
    ifTypeInUse,
} from './helper';

/**
 * Bayes Theorem: P(A|B) = P(B|A) * P(A) / P(B)
 */
/*const calculateBayesTheorem = (
    results: Map<string, number>,
    rule: string,
    calculationTypesMap: Map<string, Set<CalculationType>>,
    level: number,
    graph: IBayesianNetwork,
    steps: JSX.Element[],
    color: string
): number => {
    handeInfiniteLoop(level);

    if (results.has(rule)) {
        const cached = results.get(rule)!;
        steps.push(<span style={{ marginLeft: `${level * 3}px` }}><span style={{ color }}>P({rule})</span> = {cached}</span>);
        return cached;
    }

    addInUseRule(calculationTypesMap, rule, CalculationType.BayesTheorem);

    const [headPart, condPart] = rule.split('|').map(s => s.trim());
    if (!headPart || !condPart) return null;

    const head = headPart.split(',').map(s => s.trim()).filter(Boolean);
    const cond = condPart.split(',').map(s => s.trim()).filter(Boolean);

    const a = head.join(','); // e.g., "A,B"
    const b = cond.join(','); // e.g., "C,D"

    const bGivenA = `${b}|${a}`;
    const probA = a;
    const probB = b;

    const pBGivenA = calculateRule(results, bGivenA, calculationTypesMap, level + 1, graph, steps, color);
    const pA = calculateRule(results, probA, calculationTypesMap, level + 1, graph, steps, color);
    const pB = calculateRule(results, probB, calculationTypesMap, level + 1, graph, steps, color);

    if (pBGivenA == null || pA == null || pB == null || isNaN(pBGivenA) || isNaN(pA) || isNaN(pB)) return null;

    const result = pB === 0 ? 0 : (pBGivenA * pA / pB);

    steps.push(
        <span style={{ marginLeft: `${level * 3}px` }}>
            <span className="underline">Bayes' theorem:</span>{' '}
            <span style={{ color }}>P({a}|{b})</span> = P({b}|{a}) × P({a}) / P({b}) = {pBGivenA} × {pA} / {pB} = {result}
        </span>
    );

    results.set(rule, result);
    return result;
};*/

/**
 * The chain rule formula: P(A,B,C) = P(A|B,C) * P(B,C)
 */
const calculateChainRule = (
    results: Map<string, number>,
    rule: string,
    calculationTypesMap: Map<string, Set<CalculationType>>,
    level: number,
    graph: IBayesianNetwork,
    steps: JSX.Element[],
    color: string
) : number => {
    handeInfiniteLoop(level);

    if (results.has(rule)) {
        const existing = results.get(rule)!;
        console.log(`P(${rule}) = ${existing}`);
        steps.push(<span><span style={{ color }}>P({rule})</span> = {existing}</span>);
        return existing;
    }

    addInUseRule(calculationTypesMap, rule, CalculationType.ChainRule);

    const rawLetters = rule.split(',').map(x => x.trim());
    const letters = rawLetters.map(l => l.replace('~', ''));

    if (areNodesIndependent(letters, graph)) {
        let mul = 1;
        const probs: number[] = [];

        for (const item of rawLetters) {
            const prob = calculateRule(results, item, calculationTypesMap, level + 1, graph, steps, color);
            if (prob == null || isNaN(prob)) return null;
            mul *= prob;
            probs.push(prob);
        }

        const msg = `Chain rule (independent): P(${rule}) = ${rawLetters.map(x => `P(${x})`).join(' * ')} = ${probs.join(' * ')} = ${mul}`;
        console.log(msg);
        steps.push(<span><span className="underline">Chain rule (independent)</span>: <span style={{ color }}>P({rule})</span> = {probs.join(' * ')} = {mul}</span>);
        results.set(rule, mul);
        return mul;
    }

    // Use recursive chain rule based on topological ordering
    const visited = new Set<string>();
    const ordered: string[] = [];

    const dfs = (node: string) => {
        if (visited.has(node)) return;
        for (const parent of graph[node]?.parents ?? []) {
            dfs(parent);
        }
        visited.add(node);
        ordered.push(node);
    };

    for (const node of rawLetters) {
        dfs(node.replace('~', ''));
    }

    const rules: string[] = [];

    for (const node of ordered) {
        const isNegated = rawLetters.includes(`~${node}`);
        if (!rawLetters.includes(node) && !isNegated) continue;

        const parents = graph[node]?.parents ?? [];
        const conditioned = parents
            .filter(p => rawLetters.includes(p) || rawLetters.includes(`~${p}`))
            .map(p => rawLetters.includes(p) ? p : `~${p}`)
            .join(',');

        const head = isNegated ? `~${node}` : node;
        const formula = conditioned ? `${head}|${conditioned}` : head;
        rules.push(formula);
    }

    const rulesStr = rules.map(item => `P(${item})`).join(' * ');
    console.log(`Start ${level}: Chain rule: P(${rule}) = ${rulesStr}`);

    let mul = 1;
    const probs: number[] = [];

    for (const ruleItem of rules) {
        const prob = calculateRule(results, ruleItem, calculationTypesMap, level + 1, graph, steps, color);
        if (prob == null || isNaN(prob)) return null;
        mul *= prob;
        probs.push(prob);
    }

    console.log(`End ${level}: Chain rule: P(${rule}) = ${rulesStr} = ${probs.join(' * ')} = ${mul}`);
    steps.push(<span><span className="underline">Chain rule</span>: <span style={{ color }}>P({rule})</span> = {rulesStr} = {probs.join(' * ')} = {mul}</span>);
    results.set(rule, mul);
    return mul;
};

const calculateShortcutChainRule = (
    results: Map<string, number>,
    rule: string,
    calculationTypesMap: Map<string, Set<CalculationType>>,
    level: number,
    graph: IBayesianNetwork,
    steps: JSX.Element[],
    color: string
): number | null => {
    handeInfiniteLoop(level);

    if (results.has(rule)) {
        const existing = results.get(rule)!;
        console.log(`P(${rule}) = ${existing}`);
        steps.push(<span><span style={{ color }}>P({rule})</span> = {existing}</span>);
        return existing;
    }

    addInUseRule(calculationTypesMap, rule, CalculationType.ChainRuleShortcut);

    const rawVars = rule.split(',').map(s => s.trim()).filter(Boolean);
    if (rawVars.length < 2) return null; // must have at least head + one rest

    const head = rawVars[0];
    const rest = rawVars.slice(1).join(',');

    const conditionalRule = `${head}|${rest}`;

    if (!results.has(conditionalRule)) return null;

    const pCond = calculateRule(results, conditionalRule, calculationTypesMap, level + 1, graph, steps, color);
    const pRest = calculateRule(results, rest, calculationTypesMap, level + 1, graph, steps, color);

    if (pCond == null || pRest == null || isNaN(pCond) || isNaN(pRest)) return null;

    const result = pCond * pRest;
    steps.push(
        <span style={{ marginLeft: `${level * 3}px` }}>
            <span className="underline">Chain rule (shortcut)</span>: <span style={{ color }}>P({rule})</span> = P({conditionalRule}) * P({rest}) = {pCond} * {pRest} = {result}
        </span>
    );

    results.set(rule, result);
    return result;
};

/**
 * conditional probability formula: P(A|B) = P(A,B) / P(B)
 */
const calculateConditionalProbability = (
    results: Map<string, number>,
    rule: string,
    calculationTypesMap: Map<string, Set<CalculationType>>,
    level: number,
    graph: IBayesianNetwork,
    steps: JSX.Element[],
    color: string
) : number => {

    handeInfiniteLoop(level);

    if(results.has(rule)) {
        console.log(`P(${ rule }) = ${ results.get(rule) }`);
        steps.push(<span style={{ marginLeft: `${ level * 3 }px` }}><span style={{ color }}>P({ rule })</span> = { results.get(rule) }</span>);
        return (results.get(rule) ?? null);
    }

    addInUseRule(calculationTypesMap, rule, CalculationType.ConditionalProbability);

    const [node, condition] = rule.split('|').map(item => item.trim());

    const rule1 = `${ node },${ condition }`;
    const rule2 = condition;

    console.log(`Start ${ level }: Conditional probability: P(${ rule }) = P(${ rule1 }) / P(${ rule2 })`);
    // steps.push(`Start ${ level }: Conditional probability: P(${ rule }) = P(${ rule1 }) / P(${ rule2 })`);

    // console.log(`P(${ rule }) = P(${ rule1 }) / P(${ rule2 })`);

    const prob1 = calculateRule(results, rule1, calculationTypesMap, level + 1, graph, steps, color);
    if(prob1 == null || isNaN(prob1)) return null;

    const prob2 = calculateRule(results, rule2, calculationTypesMap, level + 1, graph, steps, color);
    if(prob2 == null || isNaN(prob2)) return null;

    const result = prob2 === 0 ? 0 : (prob1 / prob2);

    console.log(`End ${ level }: Conditional probability: P(${ rule }) = P(${ rule1 }) / P(${ rule2 }) = ${ prob1 } / ${ prob2 } = ${ result }`);
    steps.push(<span style={{ marginLeft: `${ level * 3 }px` }}><span className="underline">Conditional probability:</span> <span style={{ color }}>P({ rule })</span> = P({ rule1 }) / P({ rule2 }) = { prob1 } / { prob2 } = { result }</span>);
    results.set(rule, result);
    return result;
};

/**
 * full marginal expansion formula: P(C) = P(C,A,B) + P(C,~A,B) + P(C, A,~B) + P(C, ~A,~B)
 */
const calculateFullMarginalExpansion = (
    results: Map<string, number>,
    rule: string,
    calculationTypesMap: Map<string, Set<CalculationType>>,
    level: number,
    graph: IBayesianNetwork,
    steps: JSX.Element[],
    color: string
) : number => {

    handeInfiniteLoop(level);

    if(results.has(rule)) {
        console.log(`P(${ rule }) = ${ results.get(rule) }`);
        steps.push(<span style={{ marginLeft: `${ level * 3 }px` }}><span style={{ color }}>P({ rule })</span> = { results.get(rule) }</span>);
        return (results.get(rule) ?? null);
    }

    addInUseRule(calculationTypesMap, rule, CalculationType.FullMarginalExpansion);

    const rules = getRulesForMarginalExpansion(results, rule);
    if(rules.length > 0) {

        const concat = rules.map(item => `P(${ item })`).join(' + ');
        // console.log(rule, node, rules)
        console.log(`Start ${ level }: Full marginal expansion formula: P(${ rule }) = ${ concat }`);
        // steps.push(`Start ${ level }: Full marginal expansion formula: P(${ rule }) = ${ concat }`);

        let sum = 0;
        const sumItems: number[] = [];

        for(const item of rules) {
            const prob = calculateRule(results, item, calculationTypesMap, level + 1, graph, steps, color);
            if(prob == null || isNaN(prob)) return null;
            sum += prob;
            sumItems.push(prob);
        }

        console.log(`End ${ level }: Full marginal expansion formula: P(${ rule }) = ${ concat } = ${ sumItems.join(' + ')} = ${ sum }`);
        steps.push(<span style={{ marginLeft: `${ level * 3 }px` }}><span className="underline">Full marginal expansion formula:</span> <span style={{ color }}>P({ rule })</span> = { concat } = { sumItems.join(' + ')} = { sum }</span>);

        results.set(rule, sum);
        return sum;
    }
    else{
        const possibleLetters = findMarginComplementRules(results, rule);
        if(possibleLetters.length > 0) {
            const possibleLetter = possibleLetters[0].replace('~', '');

            const rule1 = `${ rule },${ possibleLetter }`;
            const rule2 = `${ rule },~${ possibleLetter }`;
            console.log(`Start ${ level }: Full marginal expansion formula: P(${ rule }) = P(${ rule1 }) + P(${ rule2 })`);

            const prob1 = calculateRule(results, rule1, calculationTypesMap, level + 1, graph, steps, color);
            if(prob1 == null || isNaN(prob1)) return null;

            const prob2 = calculateRule(results, rule2, calculationTypesMap, level + 1, graph, steps, color);
            if(prob2 == null || isNaN(prob2)) return null;

            const result = prob1 + prob2;

            console.log(`End ${ level }: Full marginal expansion formula: P(${ rule }) = P(${ rule1 }) + P(${ rule2 }) = ${ prob1 } + ${ prob2 } = ${ result }`);
            steps.push(<span style={{ marginLeft: `${ level * 3 }px` }}><span className="underline">Full marginal expansion formula</span>: <span style={{ color }}>P({ rule })</span> = P({ rule1 }) + P({ rule2 }) = { prob1 } + { prob2 } = { result }</span>);
            results.set(rule, result);
            return result;
        }

    }

    return null;
};

const handleNegateRules = (
    results: Map<string, number>,
    rule: string,
    calculationTypesMap: Map<string, Set<CalculationType>>,
    level: number,
    graph: IBayesianNetwork,
    steps: JSX.Element[],
    color: string
) : number | null  => {

    addInUseRule(calculationTypesMap, rule, CalculationType.Negate);

    // Rule is 'A' letter ---> data has '~A'
    if(rule.length === 1) {
        const negated = `~${ rule }`;
        const result = calculateRule(results, negated, calculationTypesMap, level + 1, graph, steps, color);
        if(result !== null && !isNaN(result)) {
            results.set(rule, 1 - result);
            return 1 - result;
        }
    }

    // Rule is '~A' letter ---> data has 'A'
    if(rule.length === 2 && rule[0] === '~') {
        const negated = rule.replace('~', '');
        const result = calculateRule(results, negated, calculationTypesMap, level + 1, graph, steps, color);
        if(result !== null && !isNaN(result)) {
            results.set(rule, 1 - result);
            return 1 - result;
        }
    }

    if (rule.includes('|')) {
        const [head, condition] = rule.split('|').map(s => s.trim());

        const isNegated = head.startsWith('~');
        const plainHead = isNegated ? head.slice(1) : head;

        // Only handle negated head like ~A|B
        if (isNegated) {
            const complementRule = `${plainHead}|${condition}`;
            const result = calculateRule(results, complementRule, calculationTypesMap, level + 1, graph, steps, color);
            if(result !== null && !isNaN(result)) {
                // Check that B is a direct parent of A in the graph
                const parents = graph[plainHead]?.parents ?? [];
                const conditionVars = condition.split(',').map(s => s.replace('~', ''));
                const isValid = conditionVars.every(c => parents.includes(c));

                if (isValid) {
                    const final = 1 - result;
                    results.set(rule, final);
                    return final;
                }
            }
        }
    }

    return null;
};

export const calculateRule = (
    results: Map<string, number>,
    rule: string,
    calculationTypesMap: Map<string, Set<CalculationType>>,
    level: number,
    graph: IBayesianNetwork,
    steps: JSX.Element[],
    color: string
): number | null => {

    handeInfiniteLoop(level);

    if(results.has(rule)) {
        console.log(`P(${ rule }) = ${ results.get(rule) }`);
        // steps.push(`P(${ rule }) = ${ results.get(rule) }`);
        return (results.get(rule) ?? null);
    }

    if(!ifTypeInUse(calculationTypesMap, rule, CalculationType.Negate)) {
        const negatedResult = handleNegateRules(results, rule, calculationTypesMap, level + 1, graph, steps, color);
        if(negatedResult !== null && !isNaN(negatedResult)) return negatedResult;
    }

    const isOneLetter = rule.length === 1;
    if(isOneLetter && !ifTypeInUse(calculationTypesMap, rule, CalculationType.FullMarginalExpansion)) {
        // Try full marginal expansion formula: P(C) = P(C,A,B) + P(C,~A,B) + P(C, A,~B) + P(C, ~A,~B)
        console.log(`${ rule } is one letter - try full marginal expansion formula`);
        const result = calculateFullMarginalExpansion(results, rule, calculationTypesMap, level + 1, graph, steps, color);
        if(result !== null && !isNaN(result)) {
            results.set(rule, result);
            return result;
        }
    }

    if(rule.includes('|')) {
        if(!ifTypeInUse(calculationTypesMap, rule, CalculationType.ConditionalProbability)) {
            // Try conditional probability formula: P(A|B) = P(A,B) / P(B)
            // console.log(`${ rule } has condition - try conditional probability formula`);
            const result = calculateConditionalProbability(results, rule, calculationTypesMap, level + 1, graph, steps, color);
            if(result !== null && !isNaN(result)) {
                results.set(rule, result);
                return result;
            }
        }

        /*if (!ifTypeInUse(calculationTypesMap, rule, CalculationType.BayesTheorem)) {
            const result = calculateBayesTheorem(results, rule, calculationTypesMap, level + 1, graph, steps, color);
            if (result !== null && !isNaN(result)) {
                results.set(rule, result);
                return result;
            }
        }*/
    }

    if(rule.includes(',') && !rule.includes('|')) {

        //const letters = rule.split(',').map(item => item.trim());
        //sortLettersPerGraph(letters, graph);

        if(!ifTypeInUse(calculationTypesMap, rule, CalculationType.ChainRule)) {
            // Try chain rule formula: P(A,B) = P(A|B) * P(B)
            // console.log(`${ rule } has comma - try chain rule formula`);
            const result = calculateChainRule(results, rule, calculationTypesMap, level + 1, graph, steps, color);
            if(result !== null && !isNaN(result)) {
                results.set(rule, result);
                return result;
            }
        }

        if(!ifTypeInUse(calculationTypesMap, rule, CalculationType.ChainRuleShortcut)) {
            const result = calculateShortcutChainRule(results, rule, calculationTypesMap, level + 1, graph, steps, color);
            if(result !== null && !isNaN(result)) {
                results.set(rule, result);
                return result;
            }
        }

        if(!ifTypeInUse(calculationTypesMap, rule, CalculationType.FullMarginalExpansion)) {
            // Try full marginal expansion formula: P(A,B) = P(A,B,~C) + P(A,B,C)
            const result = calculateFullMarginalExpansion(results, rule, calculationTypesMap, level + 1, graph, steps, color);
            if(result !== null && !isNaN(result)) {
                results.set(rule, result);
                return result;
            }
        }
    }


    return null;
};